/** Watch lanes on the MV clock. Every region is a labelled button that seeks (numeric twins: the transport's
 * prev/next buttons and the In/Out inputs); clicking empty lane space seeks too. Shift-click on a section, lyric
 * line, cut, hot window, clip or suggestion sets In to its start, Alt-click sets Out to its end (a line: 120 ms
 * lead-in / 250 ms hold + 700 ms fade, the clip rule). Dense marks (beat-layer ticks, heat bins, words, cut ticks)
 * are aria-hidden, with text twins elsewhere (the beats table and per-layer prev/next buttons, the heat table, the
 * lyrics panel, the cuts table). One lane per beat layer (bars, beats, 8ths, 16ths, kick, snare, hi-hat), each tick
 * placed at its integer ms (`data-t`); the Cuts lane has the MV's picture transitions (video.scenes). */
import { useCallback, useMemo, useRef, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { useAnnounce } from "../../components/announce";
import { fmtMs } from "../../lib/frames";
import type { FrameController } from "../player/controller";
import type { InOut } from "../player/Player";
import { usePlayerSelect } from "../player/usePlayer";
import type { MvClipOut, Suggestion, Timeline } from "../types";
import {
  BEAT_LAYERS, clickAction, LANE_LABELS, LANES, layerPos, layerTimes, lineAt, lineEdges, pct, projectCutscenes,
  projectHeat, projectLines, projectScenes, projectSections, projectTicks, projectWords, SCENE_KIND_LABELS,
  timeAtFraction, viewFor, viewPage, wordIndexAt, type BeatLayer, type Edges, type Lane, type TextMode, type View,
} from "./project";
import { WaveformLane } from "./WaveformLane";

export const ZOOMS: { s: number; label: string }[] = [
  { s: 0, label: "Whole video" }, { s: 60, label: "60 s" }, { s: 30, label: "30 s" }, { s: 20, label: "20 s" },
  { s: 10, label: "10 s" },
];

const at = (left: number, width?: number): CSSProperties =>
  width == null ? { left: `${left}%` } : { left: `${left}%`, width: `${width}%` };

/** The lanes' viewport (whole MV or zoom pages that follow the playhead); the beats table shares it. */
export function useLaneView(ctrl: FrameController, zoom: number, durationMs: number): View {
  const page = usePlayerSelect(ctrl, (s) => viewPage(s.tMs, zoom));
  return useMemo(() => viewFor(page * zoom * 1000, durationMs, zoom), [page, zoom, durationMs]);
}

export type EdgeClick = (e: MouseEvent<HTMLElement>, edges: Edges, what: string) => void;

/** Click on a timed item: plain = seek to its start, Shift = In, Alt = Out (announced). `inOut` may change
 * identity on every URL write; the handler stays stable. */
export function useEdgeClick(ctrl: FrameController, inOut?: InOut): EdgeClick {
  const announce = useAnnounce();
  const ref = useRef(inOut);
  ref.current = inOut;
  return useCallback((e, edges, what) => {
    const io = ref.current;
    const act = io ? clickAction(e) : "seek";
    if (act === "in" && io) {
      io.set({ in: edges.inMs });
      announce(`In set to ${fmtMs(edges.inMs)} (${what})`);
    } else if (act === "out" && io) {
      e.preventDefault();
      io.set({ out: edges.outMs });
      announce(`Out set to ${fmtMs(edges.outMs)} (${what})`);
    } else ctrl.seekMs(edges.seekMs ?? edges.inMs);
  }, [ctrl, announce]);
}

/** One lane: label + track (clicking empty track space seeks). `label` names lanes outside LANES (e.g. Vision). */
export function LaneRow({ lane, children, view, ctrl, className, label }: {
  lane: Lane | string; children: ReactNode; view: View; ctrl: FrameController; className?: string; label?: string;
}) {
  const onTrack = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    const r = e.currentTarget.getBoundingClientRect();
    if (r.width > 0) ctrl.seekMs(timeAtFraction((e.clientX - r.left) / r.width, view));
  };
  const name = label ?? LANE_LABELS[lane as Lane] ?? lane;
  return (
    <div className={`ws8-lane${className ? ` ${className}` : ""}`} role="group" aria-label={`${name} lane`}
      data-testid={`lane-${lane}`}>
      <div className="ws8-lane-label" aria-hidden="true">{name}</div>
      <div className="ws8-track" onClick={onTrack}>{children}</div>
    </div>
  );
}

export function Playhead({ ctrl, view }: { ctrl: FrameController; view: View }) {
  const t = usePlayerSelect(ctrl, (s) => s.tMs);
  if (t < view.v0 || t > view.v1) return null;
  const x = pct(t, view) / 100;
  return <div className="ws8-playhead" aria-hidden="true" data-testid="playhead"
    style={{ left: `calc(var(--ws8-label-w) + (100% - var(--ws8-label-w)) * ${x})` }} />;
}

function InOutShade({ inOut, view }: { inOut?: InOut; view: View }) {
  if (!inOut || inOut.inMs == null || inOut.outMs == null || inOut.outMs <= inOut.inMs) return null;
  const a = Math.max(view.v0, inOut.inMs);
  const b = Math.min(view.v1, inOut.outMs);
  if (b <= a) return null;
  const x = pct(a, view) / 100;
  const w = (pct(b, view) - pct(a, view)) / 100;
  return <div className="ws8-inout-shade" aria-hidden="true" style={{
    left: `calc(var(--ws8-label-w) + (100% - var(--ws8-label-w)) * ${x})`,
    width: `calc((100% - var(--ws8-label-w)) * ${w})` }} />;
}

export function LyricsLane({ tl, view, mode, ctrl, edge }: {
  tl: Timeline; view: View; mode: TextMode; ctrl: FrameController; edge: EdgeClick;
}) {
  const lines = useMemo(() => projectLines(tl, view, mode), [tl, view, mode]);
  const words = useMemo(() => projectWords(tl, view, mode), [tl, view, mode]);
  const curLine = usePlayerSelect(ctrl, (s) => lineAt(tl.lines, s.tMs)?.idx ?? -1);
  const curWord = usePlayerSelect(ctrl, (s) => (words.length ? wordIndexAt(tl.words, s.tMs) : -1));
  return (
    <>
      {lines.map((l) => (
        <button key={l.key} type="button" className="ws8-item ws8-line" style={at(l.left, l.width)}
          aria-current={l.idx === curLine ? "true" : undefined}
          aria-label={`Line ${l.idx + 1}: ${l.text} at ${fmtMs(l.t0)}${l.performer ? ` (${l.performer})` : ""}`}
          data-testid={`lane-line-${l.idx}`} onClick={(e) => edge(e, lineEdges(l.t0, l.t1), `line ${l.idx + 1}`)}>
          {words.length ? "" : l.text}
        </button>
      ))}
      {words.map((w) => (
        <span key={w.key} aria-hidden="true" className={`ws8-word${w.i === curWord ? " ws8-word-now" : ""}${
          w.flag !== "aligned" ? " ws8-word-flag" : ""}`} style={at(w.left, w.width)}>{w.text}</span>
      ))}
    </>
  );
}

function ClipItems({ items, view, edge, kind }: {
  items: { key: string; t0: number; t1: number; label: string; detail: string }[];
  view: View; edge: EdgeClick; kind: "clip" | "suggestion";
}) {
  return (
    <>
      {items.map((c) => {
        if (c.t1 <= view.v0 || c.t0 >= view.v1) return null;
        const a = Math.max(c.t0, view.v0);
        const b = Math.min(c.t1, view.v1);
        return (
          <button key={c.key} type="button" className={`ws8-item ws8-${kind}`}
            style={at(pct(a, view), Math.max(0.15, pct(b, view) - pct(a, view)))}
            aria-label={`${kind === "clip" ? "Clip" : "Suggestion"} ${c.label} ${fmtMs(c.t0)}–${fmtMs(c.t1)} ${c.detail}`}
            data-testid={`lane-${kind}-${c.key}`} onClick={(e) => edge(e, { inMs: c.t0, outMs: c.t1 }, c.label)}>
            {c.label}
          </button>
        );
      })}
    </>
  );
}

export function BeatLane({ tl, layer, view, ctrl }: { tl: Timeline; layer: BeatLayer; view: View; ctrl: FrameController }) {
  const times = useMemo(() => layerTimes(tl, layer), [tl, layer]);
  const ticks = useMemo(() => projectTicks(times, view), [times, view]);
  const barNumbers = layer === "bars" && view.v1 - view.v0 <= 30_000;
  return (
    <LaneRow lane={layer} view={view} ctrl={ctrl} className="ws8-lane-beat">
      {ticks.map((b) => (
        <span key={b.t} aria-hidden="true" className={`ws8-tick ws8-tick-${layer}`} style={at(b.left)} data-t={b.t}>
          {barNumbers && <span className="ws8-barnum">{(layerPos(tl, layer, b.i) ?? "").split(".")[0]}</span>}
        </span>
      ))}
    </LaneRow>
  );
}

export function CutsLane({ tl, view, ctrl, edge }: { tl: Timeline; view: View; ctrl: FrameController; edge: EdgeClick }) {
  const ticks = useMemo(() => projectScenes(tl, view), [tl, view]);
  return (
    <LaneRow lane="cuts" view={view} ctrl={ctrl} className="ws8-lane-cuts">
      {ticks.map((t) => (
        <button key={t.key} type="button" tabIndex={-1} aria-hidden="true" data-t={t.t} data-kind={t.kind}
          className={`ws8-cut${t.kind === "cut" ? "" : " ws8-dissolve"}`}
          style={t.width != null ? at(t.left, t.width) : at(t.left)}
          title={`${SCENE_KIND_LABELS[t.kind] ?? t.kind} ${fmtMs(t.t)} · score ${t.score.toFixed(2)}${
            t.shotLen != null ? ` · shot ${(t.shotLen / 1000).toFixed(1)} s` : ""} (Shift: In, Alt: Out)`}
          onClick={(e) => edge(e, { inMs: t.t, outMs: t.t }, `${t.kind} at ${fmtMs(t.t)}`)} />
      ))}
    </LaneRow>
  );
}

export function clipItems(clips: readonly MvClipOut[]) {
  return clips.filter((c) => c.state !== "rejected").map((c) => ({ key: String(c.id), t0: c.t_mv_in_ms,
    t1: c.t_mv_out_ms, label: c.label ?? `clip ${c.id}`, detail: `${c.state}, ${c.template_state.replace("_", " ")}` }));
}

export function suggestionItems(s: readonly Suggestion[]) {
  return s.map((x, i) => ({ key: `s${i}`, t0: x.t_mv_in_ms, t1: x.t_mv_out_ms, label: x.label,
    detail: `score ${x.score.toFixed(2)}${x.reasons.length ? `: ${x.reasons.join(", ")}` : ""}` }));
}

export function Lanes({ tl, durationMs, ctrl, lanes, setLanes, zoom, setZoom, mode, inOut, peaksMediaId,
  audioMediaId }: {
  tl: Timeline;
  durationMs: number;
  ctrl: FrameController;
  lanes: Set<Lane>;
  setLanes: (next: Set<Lane>) => void;
  zoom: number;
  setZoom: (s: number) => void;
  mode: TextMode;
  inOut?: InOut;
  peaksMediaId: number | null;
  audioMediaId: number | null;
}) {
  const view = useLaneView(ctrl, zoom, durationMs);
  const edge = useEdgeClick(ctrl, inOut);
  const sections = useMemo(() => projectSections(tl, view), [tl, view]);
  const cuts = useMemo(() => projectCutscenes(tl, view), [tl, view]);
  const heat = useMemo(() => projectHeat(tl, view), [tl, view]);
  const toggle = (l: Lane) => {
    const next = new Set(lanes);
    if (next.has(l)) next.delete(l);
    else next.add(l);
    setLanes(next);
  };
  return (
    <section className="ws8-lanes-wrap" aria-labelledby="ws8-lanes-h">
      <h2 id="ws8-lanes-h" className="ws8-h2">Lanes</h2>
      <div className="ws8-row" role="group" aria-label="Lane options">
        {LANES.map((l) => (
          <label key={l} className="ws8-inline">
            <input type="checkbox" data-testid={`lane-toggle-${l}`} checked={lanes.has(l)} onChange={() => toggle(l)} />
            {LANE_LABELS[l]}
          </label>
        ))}
        <label className="ws8-inline">
          Zoom{" "}
          <select data-testid="zoom" value={String(zoom)} onChange={(e) => setZoom(Number(e.target.value))}>
            {ZOOMS.map((z) => <option key={z.s} value={z.s}>{z.label}</option>)}
          </select>
        </label>
        <span className="ws8-muted" data-testid="lane-view">View {fmtMs(view.v0)}–{fmtMs(view.v1)}</span>
        {inOut && <span className="ws8-muted" data-testid="lanes-hint">Click: seek · Shift-click: set In · Alt-click: set Out</span>}
      </div>
      <div className="ws8-lane-stack">
        {lanes.has("wave") && (
          <WaveformLane peaksMediaId={peaksMediaId} audioMediaId={audioMediaId} durationMs={durationMs} view={view}
            ctrl={ctrl} inOut={inOut} />
        )}
        {lanes.has("sections") && (
          <LaneRow lane="sections" view={view} ctrl={ctrl}>
            {cuts.map((c) => (
              <button key={c.key} type="button" className="ws8-item ws8-cutscene" style={at(c.left, c.width)}
                aria-label={`Cutscene (${c.kind}) ${fmtMs(c.t0)}–${fmtMs(c.t1)}`} data-testid={`lane-cutscene-${c.key}`}
                onClick={(e) => edge(e, { inMs: c.t0, outMs: c.t1 }, `cutscene ${c.kind}`)}>{c.kind}</button>
            ))}
            {sections.map((s) => (
              <button key={s.key} type="button" className={`ws8-item ws8-section${s.chorus ? " ws8-chorus" : ""}`}
                style={at(s.left, s.width)} data-testid={`lane-section-${s.idx}`}
                aria-label={`Section ${s.label}${s.chorus ? " (chorus)" : ""} ${fmtMs(s.t0)}–${fmtMs(s.t1)}${
                  s.performer ? `, ${s.performer}` : ""}`}
                onClick={(e) => edge(e, { inMs: s.t0, outMs: s.t1 }, `section ${s.label}`)}>{s.label}</button>
            ))}
          </LaneRow>
        )}
        {lanes.has("cuts") && <CutsLane tl={tl} view={view} ctrl={ctrl} edge={edge} />}
        {lanes.has("lyrics") && (
          <LaneRow lane="lyrics" view={view} ctrl={ctrl}>
            <LyricsLane tl={tl} view={view} mode={mode} ctrl={ctrl} edge={edge} />
          </LaneRow>
        )}
        {BEAT_LAYERS.filter((l) => lanes.has(l)).map((l) => (
          <BeatLane key={l} tl={tl} layer={l} view={view} ctrl={ctrl} />
        ))}
        {lanes.has("heat") && (
          <LaneRow lane="heat" view={view} ctrl={ctrl}>
            {heat.map((h) => (
              <span key={h.key} aria-hidden="true" className="ws8-heat" style={{ ...at(h.left, h.width),
                opacity: 0.15 + 0.85 * h.intensity }} />
            ))}
            {tl.hot_windows.map((w, i) => {
              if (w.t1_mv_ms <= view.v0 || w.t0_mv_ms >= view.v1) return null;
              const a = Math.max(view.v0, w.t0_mv_ms);
              const b = Math.min(view.v1, w.t1_mv_ms);
              return (
                <button key={i} type="button" className="ws8-item ws8-hot" data-testid={`lane-hot-${i}`}
                  style={at(pct(a, view), pct(b, view) - pct(a, view))}
                  aria-label={`Hot TikTok window ${fmtMs(w.t0_mv_ms)}–${fmtMs(w.t1_mv_ms)} score ${w.score.toFixed(2)}`}
                  onClick={(e) => edge(e, { inMs: w.t0_mv_ms, outMs: w.t1_mv_ms }, "hot window")}>
                  hot {w.score.toFixed(2)}</button>
              );
            })}
          </LaneRow>
        )}
        {lanes.has("clips") && (
          <LaneRow lane="clips" view={view} ctrl={ctrl}>
            <ClipItems items={clipItems(tl.clips)} view={view} edge={edge} kind="clip" />
          </LaneRow>
        )}
        {lanes.has("suggestions") && (
          <LaneRow lane="suggestions" view={view} ctrl={ctrl}>
            <ClipItems items={suggestionItems(tl.suggestions)} view={view} edge={edge} kind="suggestion" />
          </LaneRow>
        )}
        <InOutShade inOut={inOut} view={view} />
        <Playhead ctrl={ctrl} view={view} />
      </div>
    </section>
  );
}
